/**
 * 顶栏右侧两个下拉：企业切换、用户（个人 / 联系客服 / 退出）。
 * 多企业才出企业下拉；一家就只显示企业名。切企业后清掉 Query 缓存，避免串租户数据。
 */
import { useState } from 'react';
import { Dropdown, App as AntApp } from 'antd';
import { SwapOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listTenants } from '@/api/endpoints/system';
import { ApiError } from '@/api/client';
import { qk } from '@/api/query';
import { useAuth } from '@/stores/auth';
import { CONSOLE_HOME } from '@/lib/paths';
import { SupportDialog } from './SupportEntry';
import { getSupport } from '@/api/endpoints/system';

export function TenantSwitch() {
  const { message } = AntApp.useApp();
  const me = useAuth((s) => s.me);
  const switchTenant = useAuth((s) => s.switchTenant);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const tenantsQuery = useQuery({
    queryKey: qk.tenants(),
    queryFn: ({ signal }) => listTenants(signal),
    enabled: Boolean(me),
    staleTime: 60_000,
  });

  const tenants = tenantsQuery.data?.items ?? [];
  const currentId = me?.tenant.id ?? '';
  const currentName = me?.tenant.name ?? '';

  if (tenants.length <= 1) {
    return <span className="cshell-tenant">{currentName}</span>;
  }

  const doSwitch = async (tenantId: string) => {
    if (!tenantId || tenantId === currentId) return;
    try {
      await switchTenant(tenantId);
      qc.clear();
      void navigate(CONSOLE_HOME, { replace: true });
      void message.success('已切换企业');
    } catch (e) {
      void message.error(e instanceof ApiError ? e.message : '切换企业失败');
    }
  };

  return (
    <Dropdown
      trigger={['click']}
      menu={{
        selectable: true,
        selectedKeys: currentId ? [currentId] : [],
        // 在某家被停用就切不进去：置灰并写明原因，不让人点了再吃一个 403
        items: tenants.map((t) => ({
          key: t.id,
          label: t.member_status === 'disabled' ? `${t.name}（已停用）` : t.name,
          disabled: t.member_status === 'disabled',
        })),
        onClick: (info) => void doSwitch(info.key),
      }}
    >
      <button type="button" className="cshell-icon-btn" aria-label="切换企业">
        <SwapOutlined />
        <span className="cshell-tenant">{currentName || '切换企业'}</span>
      </button>
    </Dropdown>
  );
}

export function UserMenu() {
  const me = useAuth((s) => s.me);
  const logout = useAuth((s) => s.logout);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [supportOpen, setSupportOpen] = useState(false);

  const support = useQuery({
    queryKey: ['system', 'support'],
    queryFn: ({ signal }) => getSupport(signal),
    enabled: supportOpen,
    staleTime: 60_000,
    retry: false,
  });

  const doLogout = async () => {
    qc.clear();
    await logout();
    void navigate('/login', { replace: true });
  };

  return (
    <>
      <Dropdown
        trigger={['click']}
        menu={{
          items: [
            { key: 'profile', label: '个人' },
            { key: 'support', label: '联系客服' },
            { type: 'divider' },
            { key: 'logout', label: '退出登录', danger: true },
          ],
          onClick: (info) => {
            if (info.key === 'profile') void navigate('/account/profile');
            else if (info.key === 'support') setSupportOpen(true);
            else if (info.key === 'logout') void doLogout();
          },
        }}
      >
        <button type="button" className="cshell-icon-btn" aria-label="用户菜单">
          <UserOutlined />
          <span className="cshell-tenant">{me?.user.nickname ?? ''}</span>
        </button>
      </Dropdown>
      <SupportDialog
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        ready={Boolean(support.data?.phone || support.data?.wechat)}
        loading={support.isFetching}
        phone={support.data?.phone ?? null}
        wechat={support.data?.wechat ?? null}
        hours={support.data?.hours ?? null}
      />
    </>
  );
}
